import {ChangeDetectionStrategy, Component, computed, effect, inject, signal} from '@angular/core';
import {Router} from '@angular/router';
import {FormControl} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {debounceTime} from 'rxjs';
import {ContactGrid} from '../contact-grid/contact-grid';
import {DirectoryStore} from '../../services/directory-store';
import {Loader} from '../../../../components/loader/loader';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {Search} from '../../../../components/search/search';
import {FilterContainer} from '../../../../components/filter-container/filter-container';
import {RouteUri} from '../../../../constants/route-uri';
import {ConfirmService} from '../../../../services/confirm.service';
import {ContactSummary, CustomerState} from '../../../../../api';
import {CustomerStateDropdown} from '../customer-state-dropdown/customer-state-dropdown';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
  selector: 'elcia-directory-contacts',
  imports: [
    ContactGrid,
    Loader,
    ElciaButton,
    Search,
    FilterContainer,
    CustomerStateDropdown,
    TranslatePipe,
  ],
  templateUrl: './directory-contacts.html',
  styleUrl: './directory-contacts.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DirectoryContacts {
  private readonly directoryStore = inject(DirectoryStore);
  private readonly router = inject(Router);
  private readonly confirmService = inject(ConfirmService);

  protected readonly contacts = computed(() => this.directoryStore.contactSummaries());
  protected readonly isLoading = computed(() => this.directoryStore.isContactsLoading());

  protected readonly isFilterDisplayed = signal(false);

  protected readonly searchControl = new FormControl('', {nonNullable: true});
  protected readonly statesControl = new FormControl<CustomerState[]>([CustomerState.IsActive], {nonNullable: true});

  private readonly searchValue = toSignal(
    this.searchControl.valueChanges.pipe(debounceTime(300)),
    {initialValue: this.searchControl.value}
  );

  private readonly statesValue = toSignal(
    this.statesControl.valueChanges,
    {initialValue: this.statesControl.value}
  );

  private readonly syncSearch = effect(() => {
    this.directoryStore.contactSearchTerm.set(this.searchValue() || null);
  });

  private readonly syncStates = effect(() => {
    this.directoryStore.contactStates.set(this.statesValue());
  });

  protected toggleFilterDisplayed(): void {
    this.isFilterDisplayed.update(v => !v);
  }

  constructor() {
    this.directoryStore.refreshContactsIfStale();
  }

  protected async navToForm() {
    await this.router.navigate(['/' + RouteUri.DIRECTORY_FORM, RouteUri.DIRECTORY_FORM_CONTACT]);
  }

  protected async onContactSelected(contact: ContactSummary) {
    await this.router.navigate(['/' + RouteUri.DIRECTORY_FORM, RouteUri.DIRECTORY_FORM_CONTACT, contact.id]);
  }

  protected onContactDelete(contact: ContactSummary): void {
    this.confirmService.confirm({
      title: 'entity.contact',
      message: `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim(),
      accept: async () => {
        await this.directoryStore.deleteContactMutation.mutateAsync(contact.id);
      }
    });
  }
}
